import React, { useState, useContext, Fragment } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Grid from "@mui/material/Grid";
import { useNavigate } from "react-router-dom";
import MatchRatio from "../../../../components/MatchRatio";
import { BigHeadline, NormalBoldText } from "../../../../components/TextParts";
import InfoParts from "../molecules/InfoParts";
import { Box } from "@mui/system";
import { CloseIcon, OpenIcon } from "../../../../components/AccordionIcon";
import {
  FormatDateString,
  FindLatestDate,
} from "../../../../functions/toISOStringWithTimezone";
import { styled } from "@mui/material/styles";
import { dashboardContext } from "../../Dashboard";
import PersonnelHiddenButton from "../molecules/PersonnelHiddenButton";
import { useLocalStorage } from "../../../../hooks/localStrage";
import { useTheme } from "@emotion/react";
import { useMediaQuery } from "@mui/material";

const CustomButton = styled(Box)({
  "&:hover": {
    backgroundColor: "#F2F3F4",
  },
});

const accordingStyles = {
  ".MuiAccordionSummary-expandIconWrapper": {
    px: "10px",
    position: "relative",
    right: "-10px",
  },
};

const HIDE_BUTTON_STATUSES = [120, 130, 140, 150, 160];

export default function MatchingHistory() {
  const { activeMatchingList = [] } = useContext(dashboardContext);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const [, setCurrentPage] = useLocalStorage("currentPage");

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Fragment>
      <BigHeadline text="アクティブな募集の商談進捗状況" />
      {activeMatchingList.map((data, index) => {
        const project = data.project || {};
        const personnels = project.personnels || [];
        const isExpanded = expanded === index;

        return (
          <Box key={data.project_id || index} sx={{ borderBottom: 1.5, borderColor: "#eee" }}>
            <Accordion
              expanded={isExpanded}
              onChange={handleChange(index)}
              sx={{
                display: "flex",
                flexDirection: "column",
                boxShadow: "none",
                ...accordingStyles,
              }}
            >
              <AccordionSummary
                expandIcon={
                  <Box>
                    {isExpanded ? <CloseIcon /> : <OpenIcon />}
                  </Box>
                }
                id={`panel-${index}-header`}
                sx={{ px: "20px", py: "5px" }}
              >
                <Box
                  sx={{ width: "100%" }}
                  onClick={() => {
                    setCurrentPage(process.env.REACT_APP_RECRUITMENTLIST_NUM);
                    navigate("/recruitmentDetail", {
                      state: { projectId: data.project_id },
                    });
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      mb: "20px",
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <Box sx={{ mr: "10px" }}>
                      <NormalBoldText
                        text={project.mail_received ? project.mail_received.substring(0, 10) : ""}
                      />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <div style={{ whiteSpace: "nowrap" }}>
                        <NormalBoldText text={project.mail_subject || ""} />
                      </div>
                    </Box>
                  </Box>
                  <Grid container>
                    <Grid item xs={12} sm={6}>
                      <InfoParts
                        head="商談中の人数:"
                        info={`${personnels.length}人`}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <InfoParts
                        head="最終更新日時:"
                        info={FindLatestDate(personnels.map((p) => p.updated_at))}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                {personnels.map((personnelData) => {
                  const showHideButton = HIDE_BUTTON_STATUSES.includes(personnelData.personnel_status);

                  return (
                    <Box key={personnelData.id}>
                      <CustomButton
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          p: "20px",
                          textAlign: "left",
                          borderTop: 1.5,
                          borderColor: "#eee",
                          width: "100%",
                          color: "#1f1f1f",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setCurrentPage(process.env.REACT_APP_PERSONNELLIST_NUM);
                          navigate("/personnelDetail", {
                            state: {
                              transitionSorce: "RecruitmentDetail",
                              personnelId: personnelData.id,
                              projectId: data.project_id,
                            },
                          });
                        }}
                      >
                        <Box sx={{ display: "block", flexGrow: 1 }}>
                          <Box sx={{ display: "flex" }}>
                            <Box sx={{ mb: "20px", flexGrow: 1 }}>
                              <NormalBoldText text={personnelData.id} />
                            </Box>
                            <Box sx={{ display: { xs: "flex", sm: "none" } }}>
                              <MatchRatio
                                ratio={personnelData.match_ratio}
                                width={{ xs: "65px", sm: "90px" }}
                                mainFontSize={{ xs: "25px", sm: "30px" }}
                                subFontSize="10px"
                              />
                            </Box>
                          </Box>
                          <Grid container spacing={isSmall ? 0 : 2}>
                            <Grid item xs={12} sm={6}>
                              <InfoParts
                                head="日付"
                                info={personnelData.mail_received?.substring(0, 10) || ""}
                              />
                              <InfoParts
                                head="商流の程度"
                                info={`${personnelData.belonging_flag_label || ""} / ${personnelData.employment_status_label || ""}`}
                              />
                              <InfoParts
                                head="ステータス"
                                info={personnelData.personnel_status_view_name || ""}
                              />
                              <InfoParts
                                head="最終更新日時"
                                info={FormatDateString(personnelData.updated_at)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <InfoParts
                                head="単価"
                                info={`${Number(personnelData.cost_high || 0).toLocaleString()}万円`}
                              />
                              <InfoParts
                                head="稼働開始予定"
                                info={personnelData.actual_work || "未定"}
                              />
                            </Grid>
                            {showHideButton && (
                              <Grid item xs={12} sx={{ display: { xs: "block", sm: "none" } }}>
                                <PersonnelHiddenButton
                                  matchingId={personnelData.matching_id}
                                  sx={{ mt: 0, height: "40px" }}
                                />
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                        <Box
                          sx={{
                            display: { xs: "none", sm: "flex" },
                            flexDirection: "column",
                            alignItems: "flex-end",
                          }}
                        >
                          <Box sx={{ pl: "40px" }}>
                            <MatchRatio
                              ratio={personnelData.match_ratio}
                              width={{ xs: "65px", sm: "90px" }}
                              mainFontSize={{ xs: "25px", sm: "30px" }}
                              subFontSize="10px"
                            />
                          </Box>
                          {showHideButton && (
                            <PersonnelHiddenButton matchingId={personnelData.matching_id} />
                          )}
                        </Box>
                      </CustomButton>
                    </Box>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          </Box>
        );
      })}
    </Fragment>
  );
}